/**
 * Earthquake Report Handler
 * Handles: Daily Report (PDF)
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

const loadLogo = (src) => new Promise((resolve) => {
  const img = new Image();
  img.src = src;
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
});

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  return dateStr.replace(/-/g, '/');
};

const getSafeValue = (val) => (val !== null && val !== undefined && val !== '') ? String(val) : '0';

export const handleEarthquakeDailyReportPDF = async (message, format, rawData) => {
  if (format !== 'pdf') return alert('Earthquake Report only supports PDF format');
  
  const doc = new jsPDF('l', 'pt', 'a4'); 
  
  const baseData = rawData?.data || rawData;
  const reportDate = message.debug?.parameters?.reported_on || baseData?.reported_on || new Date().toISOString().split('T')[0];
  const displayDate = formatDateToDDMMYYYY(reportDate);
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;

  let logoImg = null;
  try { logoImg = await loadLogo(govtLogo); } catch (e) {}
  if (logoImg) doc.addImage(logoImg, 'JPEG', centerX - 30, 20, 60, 60);

  doc.setFontSize(13);
  doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
  doc.text('Department Name', centerX, 115, { align: 'center' });
  doc.text(`Earthquake Report (Dated on ${displayDate})`, centerX, 135, { align: 'center' });

  let dailyRecords = Array.isArray(baseData?.earthquake_report) ? baseData.earthquake_report : Array.isArray(baseData?.heatwave) ? baseData.heatwave : [];
  let cumRecords = Array.isArray(baseData?.earthquake_report_cum) ? baseData.earthquake_report_cum : Array.isArray(baseData?.heatwave_cum) ? baseData.heatwave_cum : [];
  
  // ✅ CRITICAL FIX: If no new incidents today, but we have cumulative data, generate base rows so it doesn't crash!
  if (dailyRecords.length === 0 && cumRecords.length > 0) {
      dailyRecords = cumRecords.map(cum => ({
          region_id: cum.region_id || cum.dist_id,
          block_name: cum.block_name || baseData?.block_name || message.debug?.parameters?.block || 'NA',
          dist_name: cum.dist_name || baseData?.district_name || message.debug?.parameters?.district || 'NA',
          remarks: cum.remarks || 'NA'
      }));
  }

  const cumMap = {};
  cumRecords.forEach(r => { if (r.region_id || r.block_name) cumMap[r.region_id || r.block_name] = r; });

  const districtName = String(dailyRecords[0]?.dist_name || message.debug?.parameters?.district || '[DISTRICT]').toUpperCase();

  const headers = [
    'S.No.', 'Block Name', 
    'Number of\nDeaths due\nto Earthqua\nke(Today)', 'Number of\nDeaths due\nto Earthqua\nke (Cumulati\nve)',
    'Number of\nInjured\nPersons\n(Today)', 'Number of\nInjured\nPersons (\nCumulativ\ne)',
    'Number of\nHouses\nFully\nDamaged\n(Today)', 'Number of\nHouses\nFully\nDamaged\n(Cumulativ\ne)',
    'Number of\nHouses\nPartially\nDamaged\n(Today)', 'Number of\nHouses\nPartially\nDamaged\n(Cumulativ\ne)',
    'Number of\nPeople\nRescued\n(Today)', 'Number of\nPeople\nRescued (\nCumulativ\ne)',
    'Number of\nRelief\nCamps\n(Today)', 'Number of\nRelief\nCamps (C\numulative)',
    'Remarks'
  ];

  const body = dailyRecords.map((row, i) => {
    const cum = cumMap[row.region_id || row.block_name] || {};
    return [
      String(i + 1),
      row.block_name || 'NA',
      getSafeValue(row.no_of_deaths), getSafeValue(cum.no_of_deaths_cum),
      getSafeValue(row.no_of_injured_persons), getSafeValue(cum.no_of_injured_persons_cum),
      getSafeValue(row.no_of_houses_fully_damaged), getSafeValue(cum.no_of_houses_fully_damaged_cum),
      getSafeValue(row.no_of_houses_partially_damaged), getSafeValue(cum.no_of_houses_partially_damaged_cum),
      getSafeValue(row.no_of_people_rescued), getSafeValue(cum.no_of_people_rescued_cum),
      getSafeValue(row.no_of_relief_camps), getSafeValue(cum.no_of_relief_camps_cum),
      row.remarks || 'NA'
    ];
  });

  if (body.length === 0) return alert('No data available');

  autoTable(doc, {
    head: [headers],
    body: body,
    startY: 160,
    theme: 'grid',
    styles: { 
        lineColor: [0, 0, 0], 
        lineWidth: 0.1,       
        cellPadding: 3, 
        overflow: 'linebreak',
        font: 'Helvetica', 
        fontSize: 7
    },
    headStyles: { 
        fillColor: [255, 255, 255], 
        textColor: [0, 0, 0], 
        fontStyle: 'bold', 
        halign: 'left'
    },
    bodyStyles: { 
        halign: 'left'
    }
  });

  const finalY = doc.lastAutoTable.finalY || 200;
  const footerY = Math.max(finalY + 60, doc.internal.pageSize.getHeight() - 100);

  doc.setFontSize(10);
  doc.text('Letter No.:', 40, footerY);
  doc.text('Date:', 40, footerY + 20);
  doc.text('Copy:', 40, footerY + 40);
  doc.text('Signature of District Officers/', pageWidth - 250, footerY);
  doc.text('Additional District Magistrate (ADM) (Disaster Management)/', pageWidth - 250, footerY + 15);
  doc.text('Officer in Charge (District Disaster Management)/', pageWidth - 250, footerY + 30);
  doc.setFont('Helvetica', 'bold');
  doc.text(`[${districtName} DISTRICT]`, pageWidth - 250, footerY + 50);

  const now = new Date();
  const ts = `${now.toISOString().split("T")[0]}_${now.toISOString().split("T")[1].split(".")[0].replace(/:/g, "")}`;
  doc.save(`daily_earthquake_report_${ts}.pdf`);
};

export const isEarthquakeReport = (toolName) => ['earthquake_daily_report', 'earthquakedailyreport'].some(n => toolName?.toLowerCase().includes(n));
export const isEarthquakeStatus = (toolName) => ['earthquake_status', 'earthquake_daily_report_status', 'earthquakestatus'].some(n => toolName?.toLowerCase().includes(n));
export const isEarthquakeMIS = (toolName) => toolName?.toLowerCase().includes('earthquake_mis');